import type { History, Location } from 'history'
import React, { useCallback, useEffect, useState } from 'react'

import {
  Signout as IconSignout,
  Cross as IconCross,
  ArrowLeft as IconArrowLeft,
  Bars as IconBars,
  Search as IconSearch,
  SearchMinus as IconSearchMinus
} from '@/components/ui/icons'
import Input from '@/components/ui/input'
import Modal from '@/components/ui/modal'
import { useToast } from '@/components/ui/toast'
import { navbar, navbarSearchProducts } from '@/config/menu'
import useDebounce from '@/hooks/useDebounce'
import { logout } from '@/services/auth'
import { serviceProductList } from '@/services/product'
import type { ProductItem } from '@/types/product'
import { getErrorMessage } from '@/utils/error'
import { removeSession } from '@/utils/session'

type PrivateHeaderProps = {
  history: History
  location: Location
  type: string
  title?: {
    label: string
    withBack: boolean
  }
  onToggleSidebar?: () => void
  isLoggedIn?: boolean
}

const lockBodyScroll = (shouldLock: boolean) => {
  const body = document.body

  if (shouldLock) {
    body.style.height = '100vh'
    body.style.overflow = 'hidden'
  } else {
    body.style.height = ''
    body.style.overflow = ''
  }
}

const extractSearchKeyword = (pathname: string): string => {
  const matchKeyword = pathname.match(/\/product\/search\/([^/]+)/)
  if (matchKeyword?.[1]) {
    try {
      return decodeURIComponent(matchKeyword[1])
    } catch (_error) {
      return matchKeyword[1]
    }
  }
  return ''
}

const highlightText = (text: string, keyword: string): React.ReactNode => {
  if (!keyword.trim()) return text
  const regex = new RegExp(
    `(${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
    'gi'
  )
  const parts = text.split(regex)
  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} className="bg-transparent text-biome-green-600 font-bold">
        {part}
      </mark>
    ) : (
      part
    )
  )
}

type HighlightedSuggestionItemProps = {
  item: ProductItem
  keyword: string
  onClick: () => void
}

const HighlightedSuggestionItem: React.FC<HighlightedSuggestionItemProps> = ({
  item,
  keyword,
  onClick
}) => {
  const initial = item.details?.im_name?.[0]?.toUpperCase() ?? '?'
  const returnPct = item.details?.rating_one_year

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-biome-green-50"
      data-test-id={`layout_header_button_suggestion_${item.code}`}
    >
      {item.details?.im_avatar ? (
        <img
          src={item.details.im_avatar}
          alt={item.details.im_name}
          className="size-9 shrink-0 rounded-full object-cover bg-neutral-gray-50"
        />
      ) : (
        <div className="size-9 shrink-0 rounded-full bg-purple-100 flex items-center justify-center body-xs-bold text-purple-700">
          {initial}
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p className="body-sm-regular text-neutral-gray-800 truncate">
          {highlightText(item.name, keyword)}
        </p>
        <div className="flex items-center gap-1 mt-0.5">
          <span className="body-xs-regular text-neutral-gray-500 bg-neutral-gray-75 rounded px-1.5 py-0.5 shrink-0">
            {item.details?.type}
          </span>
          <span className="text-neutral-gray-300">·</span>
          <span className="body-xs-regular text-neutral-gray-500 truncate">
            {highlightText(item.details?.im_name ?? '', keyword)}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {returnPct != null && (
          <div className="text-right">
            <p className="body-sm-bold text-biome-green-600">
              {returnPct.toFixed(2).replace('.', ',')}%
            </p>
            <p className="body-xs-regular text-neutral-gray-500">
              imbal hasil/th
            </p>
          </div>
        )}
        <span className="button-primary button-xs px-3 py-1.5 text-xs">
          Beli
        </span>
      </div>
    </button>
  )
}

const PrivateHeader: React.FC<PrivateHeaderProps> = ({
  history,
  location,
  type = '1',
  title = {
    label: '',
    withBack: false
  },
  onToggleSidebar,
  isLoggedIn = false
}) => {
  const toast = useToast()
  const [isFlexSpaceBetween, setIsFlexSpaceBetween] = useState(false)
  const [isShowSearch, setIsShowSearch] = useState(false)
  const [isOpenCS, setIsOpenCS] = useState(false)
  const [isSuggestionOpen, setIsSuggestionOpen] = useState(false)
  const [isSuggestionFocused, setIsSuggestionFocused] = useState(false)
  const [suggestions, setSuggestions] = useState<ProductItem[]>([])
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 500)

  const hasLogo = type === '2'

  const handleNavigateHome = useCallback(() => {
    history.push(process.env.REACT_APP_PREFIX ?? '/')
  }, [history])

  const handleNavigateBack = useCallback(() => {
    history.goBack()
  }, [history])

  const handleCloseSuggestions = useCallback(() => {
    setIsSuggestionOpen(false)
    setIsSuggestionFocused(false)
    lockBodyScroll(false)
  }, [])

  const handleFocusSuggestions = useCallback(() => {
    setIsSuggestionFocused(true)
    lockBodyScroll(true)
  }, [])

  const handleSuggestionSelect = useCallback(
    (code: string) => {
      history.push(`${process.env.REACT_APP_PREFIX}/product/mutualfund/${code}`)
      handleCloseSuggestions()
    },
    [handleCloseSuggestions, history]
  )

  const handleClear = useCallback(() => {
    setSearch('')
    setSuggestions([])
    handleCloseSuggestions()
  }, [handleCloseSuggestions])

  const handleClickNavbar = useCallback(
    ({ id, pathname }: { id: string; pathname: string | null }) => {
      if (id === 'cs') {
        setIsOpenCS(true)
      } else if (pathname) {
        history.push(pathname)
      }
    },
    [history]
  )

  const handleLogout = useCallback(async () => {
    try {
      const response = await logout()
      if (response?.status === 200) {
        removeSession('b-bfb')
        removeSession('b-bfb-login')
        removeSession('b-bfb-ph')
        removeSession('otp-attm')
        localStorage.removeItem('b-bfb-progold')
        sessionStorage.removeItem('b-bfb-cd')
        window.location.href = '/'
      }
    } catch (error) {
      const message = getErrorMessage(error)
      toast({
        description: message
      })
    }
  }, [toast])

  useEffect(() => {
    const trimmed = debouncedSearch.trim()

    if (trimmed.length < 3) {
      setSuggestions([])
      setIsSuggestionOpen(false)
      return
    }

    const fetchSuggestions = async () => {
      setIsFetchingSuggestions(true)
      setIsSuggestionOpen(true)
      try {
        const response = await serviceProductList({ search: trimmed, size: 5 })
        setSuggestions(response?.data?.data?.products ?? [])
      } catch (error) {
        toast({ description: getErrorMessage(error) })
        setSuggestions([])
        handleCloseSuggestions()
      } finally {
        setIsFetchingSuggestions(false)
      }
    }

    fetchSuggestions()
  }, [debouncedSearch, handleCloseSuggestions, toast])

  useEffect(() => {
    const currentPath = location.pathname
    const shouldShowSearch = navbarSearchProducts.list.includes(currentPath)

    setIsShowSearch(shouldShowSearch)
    setIsFlexSpaceBetween(
      currentPath.includes('subscription') ||
        currentPath.includes('redemption') ||
        !!title?.label
    )
    setSearch(extractSearchKeyword(location.pathname))
    handleCloseSuggestions()
  }, [handleCloseSuggestions, location.pathname, title])

  useEffect(() => {
    lockBodyScroll(false)
  }, [])

  return (
    <nav
      className={`header-private flex h-[76px] gap-4 sticky top-0 w-full bg-flat-white ${isShowSearch || isFlexSpaceBetween ? 'justify-between' : 'justify-end'} px-4 md:px-10 py-4 z-[9]`}
      style={{
        boxShadow:
          '0px 6px 16px 0px rgba(61, 71, 81, 0.06), 0px 0px 4px 1px rgba(61, 71, 81, 0.04)'
      }}
    >
      {onToggleSidebar && (
        <button
          type="button"
          onClick={onToggleSidebar}
          className="lg:hidden flex items-center justify-center p-2 text-neutral-gray-800 hover:text-biome-green-600 mr-auto"
          aria-label="Toggle sidebar"
          data-test-id="layout_header_button_toggle_sidebar"
        >
          <IconBars className="size-6" />
        </button>
      )}
      {hasLogo && (
        <div
          className={`items-center ${!title?.label ? 'mr-auto' : ''} ${title.label ? 'hidden lg:flex' : 'flex'}`}
        >
          <img
            onClick={handleNavigateHome}
            className="w-[112px] cursor-pointer"
            alt="Logo Bareksa"
            title="Logo Bareksa"
            src="/bareksa-partner-of-grab.png"
          />
        </div>
      )}

      {title.label && (
        <div className="flex items-center gap-4 w-full max-w-[600px] mx-auto">
          {title.withBack && (
            <IconArrowLeft
              onClick={handleNavigateBack}
              className="text-neutral-gray-800 cursor-pointer"
              data-test-id="layout_header_button_back"
            />
          )}
          <p className="subheading-m-regular flex-1 text-neutral-gray-800 text-center">
            {title.label}
          </p>
          <div />
        </div>
      )}

      {!hasLogo && isShowSearch && (
        <div className="relative w-[620px] max-w-full z-10">
          <Input>
            <Input.FieldWrapper className="w-full shadow-low border">
              <IconSearch className="size-5 text-neutral-gray-500 shrink-0" />
              <Input.Field
                placeholder="Cari nama produk atau manajer investasi..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onFocus={handleFocusSuggestions}
                data-test-id="layout_header_input_search"
              />
              {isSuggestionOpen && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-neutral-gray-400 hover:text-neutral-gray-600 shrink-0"
                  data-test-id="layout_header_button_clear_search"
                >
                  <IconCross className="size-4" />
                </button>
              )}
            </Input.FieldWrapper>
          </Input>

          {isSuggestionOpen && isSuggestionFocused && (
            <div className="absolute z-10 mt-2 w-full rounded-xl bg-flat-white shadow-high overflow-hidden">
              {isFetchingSuggestions ? (
                <div className="flex items-center justify-center py-10">
                  <div className="loading-spinner size-6" />
                </div>
              ) : suggestions.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-10 px-6 text-center">
                  <div className="flex items-center justify-center size-12 rounded-full bg-neutral-gray-50">
                    <IconSearchMinus className="size-6 text-neutral-gray-400" />
                  </div>
                  <div>
                    <p className="body-sm-bold text-neutral-gray-800">
                      Produk tidak ditemukan
                    </p>
                    <p className="body-xs-regular text-neutral-gray-500 mt-1">
                      Tidak ada produk atau MI yang cocok dengan &ldquo;
                      {search}&rdquo;. Coba kata kunci lain.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between px-4 pt-4 pb-2">
                    <p className="body-xs-bold text-neutral-gray-500 uppercase tracking-wide">
                      Hasil Pencarian
                    </p>
                    <p className="body-xs-bold text-biome-green-600 uppercase tracking-wide">
                      {suggestions.length} Produk
                    </p>
                  </div>

                  <div>
                    {suggestions.map((item, index) => (
                      <HighlightedSuggestionItem
                        key={`${item.code}-${index}`}
                        item={item}
                        keyword={search}
                        onClick={() => handleSuggestionSelect(item.code)}
                      />
                    ))}
                  </div>

                  <button
                    type="button"
                    className="w-full py-3 px-4 text-center body-sm-regular text-biome-green-600 hover:bg-biome-green-50 border-t border-neutral-gray-75"
                    onClick={() => {
                      history.push(
                        `${process.env.REACT_APP_PREFIX}/product/mutualfund?type_id=0&search=${encodeURIComponent(search)}`
                      )
                      handleCloseSuggestions()
                    }}
                    data-test-id="layout_header_button_view_all_search"
                  >
                    Lihat semua hasil untuk &ldquo;{search}&rdquo; &rarr;
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {isLoggedIn ? (
        <div
          className={`items-center gap-5 ${title.label ? 'hidden lg:flex' : 'flex'}`}
        >
          {navbar.map(({ Icon, id, pathname }, index) => (
            <Icon
              key={index}
              onClick={() =>
                handleClickNavbar({ id, pathname: pathname ?? null })
              }
              className="text-neutral-gray-600 cursor-pointer size-5 hover:text-biome-green-600"
            />
          ))}
          <IconSignout
            onClick={handleLogout}
            className="text-neutral-gray-600 cursor-pointer size-5 hover:text-biome-green-600"
            data-test-id="layout_header_button_logout"
          />
        </div>
      ) : (
        <div className="w-[112px]" />
      )}

      <Modal isOpen={isOpenCS} onClose={() => setIsOpenCS(false)} size="sm">
        <Modal.Header className="items-center">
          <Modal.Title>Butuh Bantuan ?</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Content>
          <p className="body-sm-regular text-neutral-gray-800">
            Jika membutuhkan bantuan mengenai Bareksa Bisnis, silakan hubungi
            Relationship Manager Anda atau email melalui{' '}
            <a
              href="mailto:bisnis@bareksa.com"
              className="text-biome-green-600 body-sm-bold"
              target="_blank"
              rel="noopener noreferrer"
            >
              bisnis@bareksa.com
            </a>
            .
          </p>
        </Modal.Content>
      </Modal>

      {/* {isSuggestionFocused && (
        <div
          className="fixed inset-0 z-10 bg-black/50"
          onClick={handleCloseSuggestions}
          role="presentation"
        />
      )} */}
    </nav>
  )
}

export default PrivateHeader
